
import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ContractType } from "@/lib/types";
import { ContractFormValues } from "../../form/ContractFormSchema";
import { CompanySearchInput } from "@/components/company-search";
import { CompanyDetails } from "@/lib/api/companyRegister";
import { useContractTypes } from "@/hooks/useContractTypes";
import { useCategories } from "@/hooks/useCategories";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";
import { HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  IncomingIcon, 
  OutgoingIcon, 
  EmploymentIcon, 
  CircleDotIcon,
  DirectionText
} from "@/components/contract-types/ContractDirectionIcons";

interface RequiredContractDetailsSectionProps {
  form: UseFormReturn<ContractFormValues>;
  selectedContractType: ContractType | null;
  setSelectedContractType: (type: ContractType | null) => void;
  setContractTypeModalOpen: (open: boolean) => void;
  setCategoryModalOpen: (open: boolean) => void;
  handleCompanySelect: (company: CompanyDetails) => CompanyDetails;
}

export function RequiredContractDetailsSection({
  form,
  selectedContractType,
  setSelectedContractType,
  setContractTypeModalOpen,
  setCategoryModalOpen,
  handleCompanySelect
}: RequiredContractDetailsSectionProps) {
  const { t } = useEnhancedTranslation();
  
  const { useContractTypesQuery } = useContractTypes();
  const { data: contractTypes = [] } = useContractTypesQuery();
  
  const { useCategoriesQuery } = useCategories();
  const { data: categories = [] } = useCategoriesQuery();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">{t('requiredDetails', 'Required Details')}</h3>
      </div>

      <FormField
        control={form.control}
        name="supplierName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('supplierName', 'Supplier Name')} *</FormLabel>
            <FormControl>
              <CompanySearchInput 
                defaultValue={field.value}
                onChange={field.onChange}
                onSelect={(company) => {
                  const updatedCompany = handleCompanySelect(company);
                  field.onChange(updatedCompany.name);
                }}
                className="w-full"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="contractTypeId"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              {t('contractType', 'Contract Type')} *
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <div className="space-y-2 p-1">
                      <p className="text-sm font-semibold">{t('contractDirections', 'Contract Directions')}:</p>
                      <div className="flex items-center gap-2">
                        <IncomingIcon className="h-4 w-4 text-emerald-500" />
                        <span className="text-xs">{t('incomingDirection', 'Incoming - You receive payments')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <OutgoingIcon className="h-4 w-4 text-orange-500" />
                        <span className="text-xs">{t('outgoingDirection', 'Outgoing - You make payments')}</span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </FormLabel>
            <Select
              onValueChange={(value) => {
                field.onChange(value);
                const selectedType = contractTypes.find(type => type.id === value) || null;
                setSelectedContractType(selectedType);
              }}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('selectContractType', 'Select contract type')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {contractTypes && contractTypes.length > 0 ? (
                  contractTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        {type.direction === "incoming" && <IncomingIcon className="h-4 w-4 text-emerald-500" />}
                        {type.direction === "outgoing" && <OutgoingIcon className="h-4 w-4 text-orange-500" />}
                        {type.direction === "employment" && <EmploymentIcon className="h-4 w-4 text-purple-500" />}
                        {(!type.direction || type.direction === "other") && <CircleDotIcon className="h-4 w-4 text-blue-500" />}
                        <span>{type.name}</span>
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-types-available" disabled>{t('noContractTypesAvailable', 'No contract types available')}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="link"
              className="p-0 h-auto text-xs"
              onClick={(e) => {
                e.preventDefault();
                setContractTypeModalOpen(true);
              }}
            >
              + {t('addNewContractType', 'Add new contract type')}
            </Button>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="categoryId"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('category', 'Category')} *</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={t('selectCategory', 'Select a category')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-categories-available" disabled>{t('noCategoriesAvailable', 'No categories available')}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="link"
              className="p-0 h-auto text-xs"
              onClick={(e) => {
                e.preventDefault();
                setCategoryModalOpen(true);
              }}
            >
              + {t('addNewCategory', 'Add new category')}
            </Button>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
